import { Component, OnInit } from '@angular/core';
import { StoreService } from '../../data/store.service';
import { MsgService } from '../../utils/message/msg.service';
import { Router } from '@angular/router';
import { ApiService } from '../../data/api.service';
import { UtilsService } from '../../utils/utils.service';
import { I18ns } from '../../utils/i18n/I18n';
import { Theme } from '../../data/theme.model';
import { WheelCell } from './wheel.model';
import { SharedModule } from '../../utils/shared.module';
import { KeyboardComponent } from '../../utils/keyboard/keyboard.component';
import { ThemesComponent } from '../themes/themes.component';
import { Save } from '../../data/save.model';

@Component({
  selector: 'app-wheel',
  standalone: true,
  imports: [SharedModule, KeyboardComponent, ThemesComponent],
  templateUrl: './wheel.component.html',
  styleUrl: './wheel.component.scss',
})
export class WheelComponent implements OnInit {
  wordIndex = 0; // Over 9000
  dictionary: string[] = [];
  history: string[] = [];
  cells: WheelCell[] = [];
  theme?: Theme;
  currentLetter = ' ';
  wrongLetterCounter = 0;
  isGameLost = false;
  isGameWon: boolean = false;
  gameIsPaused = false;
  protected readonly I18ns = I18ns;
  private readonly turns = 10;
  private readonly pieSize = 100 / this.turns;

  constructor(
    public readonly store: StoreService,
    private readonly msg: MsgService,
    private readonly router: Router,
    private readonly api: ApiService,
    private readonly utils: UtilsService,
  ) {}

  ngOnInit() {
    if (!this.store.theme()?.id) {
      this.msg.add({
        message: I18ns.grid.noTheme,
        severity: 'error',
      });
      return;
    }
    this.msg.clear();

    this.theme = this.store.theme();
    const storage = localStorage.getItem('wheelSave_' + this.theme?.id);
    if (storage) {
      const { dictionary, wordIndex } = JSON.parse(atob(storage)) as Save;
      this.dictionary = dictionary;
      this.wordIndex = wordIndex;
    } else {
      this.dictionary = this.utils.shuffle(this.theme.words.split(','));
    }

    this.newWord();
  }

  keyPressed(key: string) {
    if (key === 'Backspace') {
    } else {
      this.addLetter(key);
    }
  }

  getPieColor() {
    const output = [];
    const defaultColor = '255, 255, 255';
    const errorColor = '253, 114, 133';

    for (let i = 1; i <= this.turns; i++) {
      const color1 = this.wrongLetterCounter < i ? errorColor : defaultColor;
      const color2 = this.wrongLetterCounter <= i ? errorColor : defaultColor;
      output.push(`rgba(${color1}, ${i * 0.05 + 0.5}) ${i * this.pieSize}%`);
      output.push(`rgba(${color2}, ${(i + 1) * 0.05 + 0.5}) ${i * this.pieSize}%`);
    }

    output.pop();
    return `conic-gradient(${output.join(', ')})`;
  }

  private newWord() {
    if (this.dictionary.length === 0) {
      return;
    }
    this.msg.clear();
    this.history = [];
    this.wrongLetterCounter = 0;
    this.store.word.set(this.dictionary[this.wordIndex]);
    this.wordIndex++;
    this.cells = this.store
      .word()
      .split('')
      .map((letter: string) => {
        return {
          letter: letter,
          display: '',
        };
      });
  }

  private addLetter(key: string) {
    if (this.gameIsPaused) {
      return;
    }
    this.gameIsPaused = true;
    this.currentLetter = key;

    if (this.store.word().includes(key)) {
      this.cells.forEach((cell: WheelCell) => {
        if (cell.letter === key) {
          cell.display = key;
        }
      });
      setTimeout(() => {
        this.currentLetter = ' ';
      }, 500);
      this.history.push(key);

      if (this.cells.every((cell: WheelCell) => cell.display !== '')) {
        this.msg.add({
          message: I18ns.wheel.congratulations,
          severity: 'success',
        });

        this.api.updateWordFoundCount(this.theme?.id).subscribe();

        localStorage.setItem(
          'wheelSave_' + this.theme?.id,
          btoa(
            JSON.stringify({
              theme: this.theme,
              wordIndex: this.wordIndex,
              dictionary: this.dictionary,
            } as Save),
          ),
        );

        if (this.wordIndex === this.dictionary.length) {
          localStorage.removeItem('wheelSave_' + this.theme?.id);
          this.isGameWon = true;
          return;
        }

        setTimeout(() => {
          this.newWord();
          this.gameIsPaused = false;
        }, 2000);
      } else {
        this.gameIsPaused = false;
      }
    } else {
      if (this.wrongLetterCounter === this.turns - 1) {
        // === GAME LOST ===
        this.isGameLost = true;
        setTimeout(() => {
          this.router.navigate(['']);
        }, 8_000);
        return;
      }

      if (!this.history.includes(key)) {
        this.history.push(key);
        this.wrongLetterCounter++;
      }
      setTimeout(() => {
        this.currentLetter = ' ';
      }, 500);
      this.gameIsPaused = false;
    }
  }
}
